import { useEffect, useState } from "react";
import { showToast } from "../../../../../../components/CustomToast";
import { OutlinedInput } from "../../../../../../components/OutlinedInput";
import { OutlinedSelect } from "../../../../../../components/OutlinedSelect";
import { SolidButton } from "../../../../../../components/SolidButton";
import api from "../../../../../../services/api";
import { Form, Header, Line } from "./styles";

import * as Yup from "yup";
import validateFormFields from "../../../../../../utils/validateFormFields";

import {
  paymentType,
  paymentMethod,
  paymentSituationOptions,
  paymentSituationLabel,
  typeLabel,
  paymentMethodLabel,
  currencyOptions,
  currencyLabel,
} from "../../mocks";
import { CheckboxContainer } from "../Filter/styles";
import { Checkbox } from "../../../../../../components/Checkbox";
import { i18n } from "../../../../../../localization/i18n";
import { typesErrors } from "../../../../../../utils/validateFormFields/yupErrors";
import { format } from "date-fns";
import { Bill } from "../../../../../../hooks/AuthContext";

interface ICreateBill {
  transaction_code?: string;
  type?: string;
  payment_method?: string;
  payment_situation?: string;
  bill_value?: string;
  referring_period?: string;
  due_date?: Date | string;
  payment_date?: Date | string;
  is_recurrence?: boolean;
  bill_id?: string;
  currency?: string;
}

interface IHandleCreate {
  onHandleEdit: () => void;
  bill?: Bill;
}

export function EditBillForm({ onHandleEdit, bill }: IHandleCreate) {
  const [inputs, setInputs] = useState<ICreateBill>();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);

  function formatDate(date: string) {
    date.replace("Z", "");
    return format(new Date(date), "yyyy-MM-dd");
  }

  useEffect(() => {
    setInputs({
      bill_id: bill?.id,
      transaction_code: bill?.transaction_code,
      type: bill?.type,
      payment_method: bill?.payment_method,
      payment_situation: bill?.payment_situation,
      bill_value: bill?.bill_value,
      referring_period: bill?.referring_period,
      is_recurrence: bill?.is_recurrence,
      currency: bill?.currency,
      due_date: formatDate(String(bill?.due_date ?? new Date())),
      payment_date: bill?.payment_date
        ? formatDate(String(bill.payment_date))
        : undefined,
    });
  }, [bill]);

  function clearInputs() {
    setInputs({
      transaction_code: "",
      type: "",
      payment_method: "",
      payment_situation: "",
      bill_value: "",
      referring_period: "",
      is_recurrence: false,
      currency: "",
      due_date: undefined,
      payment_date: undefined,
    });
  }

  async function handleEditBill(event: React.FormEvent) {
    event.preventDefault();

    setLoading(true);

    const body = {
      transaction_code: inputs?.transaction_code,
      type: inputs?.type,
      payment_method: inputs?.payment_method,
      payment_situation: inputs?.payment_situation,
      bill_value: inputs?.bill_value,
      referring_period: inputs?.referring_period,
      due_date: inputs?.due_date,
      payment_date: inputs?.payment_date,
      is_recurrence: inputs?.is_recurrence ?? false,
      currency: inputs?.currency,
      bill_id: bill?.id,
    };

    try {
      const yup_validation = Yup.object().shape({
        type: Yup.string().required(typesErrors.required),
        payment_method: Yup.string().required(typesErrors.required),
        payment_situation: Yup.string().required(typesErrors.required),
        bill_value: Yup.number().required(typesErrors.required),
        referring_period: Yup.string().required(typesErrors.required),
        due_date: Yup.string().required(typesErrors.required),
      });

      const validation = await validateFormFields(body, yup_validation, {
        type: "",
        payment_method: "",
        payment_situation: "",
        bill_value: "",
        referring_period: "",
        is_recurrence: "",
        due_date: "",
      });

      if (validation.status === 400) {
        setErrors(validation.errors);
        showToast({
          type: "error",
          message: i18n.t("form_errors.alert"),
        });

        setLoading(false);
        return;
      }

      setErrors({});

      await api.post("/bills/edit", body).then((res) => {
        showToast({
          type: "success",
          message: "Fatura editada com sucesso",
        });
        clearInputs();
        onHandleEdit();
      });
    } catch (error) {
      showToast({
        type: "error",
        message: "Ocorreu um erro ao tentar editar fatura",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Form onSubmit={handleEditBill}>
        <Line>
          <Header>
            <span>Editar fatura</span>
          </Header>
          <div className="CheckBoxContainer">
            <CheckboxContainer checked={inputs?.is_recurrence}>
              <Checkbox
                checked={inputs?.is_recurrence ?? false}
                handleChange={() =>
                  setInputs((prevState) => ({
                    ...prevState,
                    is_recurrence: !inputs?.is_recurrence,
                  }))
                }
              />
              <span>Recorrente</span>
            </CheckboxContainer>
          </div>
        </Line>
        <OutlinedInput
          error={errors?.bill_value}
          handleChange={(event) =>
            setInputs((prevState) => ({
              ...prevState,
              bill_value: event,
            }))
          }
          inputName={"bill_value"}
          label={"Valor"}
          value={inputs?.bill_value ?? ""}
        />
        <Line>
          <OutlinedInput
            type="number"
            handleChange={(event) =>
              setInputs((prevState) => ({
                ...prevState,
                transaction_code: event,
              }))
            }
            inputName={"transaction_code"}
            label={"Código"}
            value={inputs?.transaction_code ?? ""}
          />
          <OutlinedInput
            error={errors?.referring_period}
            mask="99/9999"
            handleChange={(event) =>
              setInputs((prevState) => ({
                ...prevState,
                referring_period: event,
              }))
            }
            inputName={"referring_period"}
            label={"Mês referente"}
            value={inputs?.referring_period ?? ""}
          />
        </Line>
        <Line>
          <OutlinedInput
            error={errors?.due_date}
            type="date"
            handleChange={(event) =>
              setInputs((prevState) => ({
                ...prevState,
                due_date: event,
              }))
            }
            inputName={"due_date"}
            label={"Vencimento"}
            value={String(inputs?.due_date) ?? ""}
          />
          <OutlinedInput
            type="date"
            handleChange={(event) =>
              setInputs((prevState) => ({
                ...prevState,
                payment_date: event,
              }))
            }
            inputName={"payment_date"}
            label={"Pago em"}
            value={String(inputs?.payment_date) ?? ""}
          />
        </Line>
        <Line>
          <OutlinedSelect
            error={errors?.type}
            options={paymentType}
            label={"Tipo"}
            handleSelect={(event) =>
              setInputs((prevState) => ({
                ...prevState,
                type: event.value.toString(),
              }))
            }
            selectedOption={
              typeLabel[inputs?.type as keyof typeof typeLabel] ?? ""
            }
          />
          <OutlinedSelect
            error={errors?.payment_method}
            options={paymentMethod}
            label={"Método"}
            handleSelect={(event) =>
              setInputs((prevState) => ({
                ...prevState,
                payment_method: event.value.toString(),
              }))
            }
            selectedOption={
              paymentMethodLabel[
                inputs?.payment_method as keyof typeof paymentMethodLabel
              ] ?? ""
            }
          />
        </Line>
        <OutlinedSelect
          error={errors?.payment_situation}
          options={paymentSituationOptions}
          label={"Situação de pagamento"}
          handleSelect={(event) =>
            setInputs((prevState) => ({
              ...prevState,
              payment_situation: event.value.toString(),
            }))
          }
          selectedOption={
            paymentSituationLabel[
              inputs?.payment_situation as keyof typeof paymentSituationLabel
            ] ?? ""
          }
        />
        <OutlinedSelect
          options={currencyOptions}
          label={"Moeda"}
          handleSelect={(event) =>
            setInputs((prevState) => ({
              ...prevState,
              currency: event.value.toString(),
            }))
          }
          selectedOption={
            currencyLabel[inputs?.currency as keyof typeof currencyLabel] ?? ""
          }
        />
        <SolidButton text={"Editar"} type="submit" loading={loading} />
      </Form>
    </>
  );
}
